import { io } from "socket.io-client";

// Socket URL 결정 함수
// 빌드타임 상수(REACT_APP_SOCKET_URL)를 쓰지 않는다 — 이미지를 dev/prd 공용으로 두기 위해
// 항상 현재 origin 에서 파생한다. ALB Ingress 가 /socket.io 를 api 로 라우팅한다.
const getSocketUrl = () => {
  // 로컬 개발 환경
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  
  // 프로덕션 환경 (nginx 프록시를 통해 연결)
  return window.location.origin;
};

// 싱글톤 소켓 인스턴스
let socketInstance = null;

// 재연결 타이머 관리
let reconnectTimer = null;
const RECONNECT_TIMEOUT = 15000; // 15초 후에도 재연결 실패 시 경고

/**
 * 커스텀 이벤트 발생 함수
 */
const dispatchSocketEvent = (eventName, detail) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
};

/**
 * Socket.IO 싱글톤 인스턴스를 반환합니다.
 * 애플리케이션 전체에서 하나의 소켓 연결만 사용합니다.
 */
export const getSocket = () => {
  if (!socketInstance) {
    console.log('🔌 Socket.IO 싱글톤 인스턴스 생성:', getSocketUrl());
    
    socketInstance = io(getSocketUrl(), {
      path: '/socket.io/',
      transports: ['websocket', 'polling'], // websocket 우선 (세션 ID 문제 방지)
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 45000, // 타임아웃 시간 증가 (45초, 서버 connectTimeout과 맞춤)
      forceNew: false, // 기존 연결 재사용 (세션 유지)
      upgrade: true,
      rememberUpgrade: true, // WebSocket 업그레이드 기억
      autoConnect: true // 자동 연결 활성화
    });

    // 연결 성공 이벤트
    socketInstance.on('connect', () => {
      console.log('✅ Socket 연결 성공:', socketInstance.id);
      
      // 재연결 타이머 취소 및 경고 닫기
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      
      // 재연결 성공 이벤트 발생
      dispatchSocketEvent('socket:reconnected', { socketId: socketInstance.id });
    });

    // 연결 해제 이벤트
    socketInstance.on('disconnect', (reason) => {
      console.warn('⚠️ Socket 연결 해제:', reason);
      
      // 재연결 시도 중 이벤트 발생
      dispatchSocketEvent('socket:reconnecting', { reason });
      
      // 서버가 의도적으로 끊은 경우 즉시 경고
      if (reason === 'io server disconnect') {
        dispatchSocketEvent('socket:session-expired', { reason });
        return;
      }
      
      // 그 외의 경우 재연결 대기 타이머 시작
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      
      reconnectTimer = setTimeout(() => {
        // 15초 후에도 연결되지 않았으면 세션 만료 경고
        if (!socketInstance.connected) {
          console.error('❌ 재연결 실패 - 세션 만료');
          dispatchSocketEvent('socket:session-expired', { reason });
        }
        reconnectTimer = null;
      }, RECONNECT_TIMEOUT);
    });

    // 연결 오류 이벤트
    socketInstance.on('connect_error', (error) => {
      console.error('❌ Socket 연결 오류:', error);
      // 연결 오류는 disconnect 이벤트에서 처리되므로 여기서는 로깅만
    });

    // 재연결 시도 이벤트
    socketInstance.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 재연결 시도 ${attemptNumber}회`);
      dispatchSocketEvent('socket:reconnect-attempt', { attemptNumber });
    });

    // 재연결 실패 이벤트
    socketInstance.on('reconnect_failed', () => {
      console.error('❌ 재연결 실패');
      dispatchSocketEvent('socket:reconnect-failed', {});
    });
  }
  
  return socketInstance;
};

/**
 * Socket.IO 연결을 명시적으로 해제합니다.
 * (일반적으로 필요하지 않음, 앱 종료 시에만 사용)
 */
export const disconnectSocket = () => {
  if (socketInstance) {
    console.log('🔌 Socket 연결 해제');
    socketInstance.disconnect();
    socketInstance = null;
  }
};

export default getSocket;

